import type { SessionResult } from './overview';
import { capabilitiesForSource, type RaceRecording, type RaceSnapshot, type SessionCapabilities } from './race';
import { buildSnapshot, initialDriver, normalizeRecording } from './replay';

/**
 * The terminal result endpoint may intentionally omit process events.  Its
 * rows still carry the last known timing projection, so capabilities must be
 * inferred from that canonical result payload before the recording is
 * normalized.  Otherwise an empty event list falls back to the source's
 * default (which can mark mini-sectors unsupported) and the table drops real
 * cells that are present in the rows.
 */
function capabilitiesForResult(result: SessionResult): SessionCapabilities {
  const fallback = capabilitiesForSource(result.session.source);
  const timing = result.rows.map(row => row.timing).filter(Boolean);
  const hasMiniSectors = timing.some(value => value?.miniSectors?.some(sector => sector.length > 0)
    || value?.sectorDetails?.some(sector => sector.segments.length > 0));
  const hasSectorTimes = timing.some(value => value?.sectors?.some(sector => sector !== null)
    || value?.recentLaps?.some(lap => lap.sectors.some(sector => sector !== null)));
  return {
    ...fallback,
    miniSectors: hasMiniSectors ? 'available' : fallback.miniSectors,
    sectorTimes: hasSectorTimes ? 'available' : fallback.sectorTimes,
  };
}

/** A result is a terminal view, never a fabricated live recording or a replay seed at t=0. */
export function resultRecording(result: SessionResult): RaceRecording {
  const endedAtMs = result.endedAt ? Date.parse(result.endedAt) - Date.parse(result.session.dateStart) : NaN;
  const endBoundary = Number.isFinite(endedAtMs) && endedAtMs >= 0 ? endedAtMs : Infinity;
  // Reconnection snapshots carry receipt time. They cannot extend an already ended race.
  const events = result.events.filter(event => event.atMs <= endBoundary);
  const coverageEnd = result.coverage && result.coverage.endMs <= endBoundary ? result.coverage.endMs : 0;
  const durationMs = Math.max(0, coverageEnd, ...events.map(event => event.atMs));
  const coverage = result.coverage && { ...result.coverage, startMs: Math.min(result.coverage.startMs, durationMs), endMs: durationMs,
    gaps: result.coverage.gaps.map(gap => ({ ...gap, toMs: Math.min(gap.toMs, durationMs) })).filter(gap => gap.fromMs < gap.toMs) };
  return normalizeRecording({ schemaVersion: 1, session: result.session, drivers: result.rows.map(({ number, code, name, team, teamColor }) => ({ number, code, name, team, teamColor })),
    track: [], events, durationMs, notice: result.notice ?? null, coverage, capabilities: capabilitiesForResult(result) });
}

export function resultSnapshot(result: SessionResult): RaceSnapshot {
  const recording = resultRecording(result);
  const projected = buildSnapshot(recording, recording.durationMs);
  const excludedLateEvents = result.events.some(event => event.atMs > recording.durationMs);
  const observedDrivers = new Set(recording.events.flatMap(event => event.type === 'timing' ? [event.driverNumber] : []));
  return { ...projected, trackStatus: 'FINISHED', weather: excludedLateEvents ? projected.weather : result.weather ?? projected.weather,
    drivers: result.rows.map(row => {
      const previous = projected.drivers.find(driver => driver.number === row.number) ?? initialDriver(row);
      const timing = excludedLateEvents && observedDrivers.has(row.number) ? undefined : row.timing;
      return { ...previous, ...timing, number: row.number, code: row.code, name: row.name, team: row.team, teamColor: row.teamColor,
        position: row.position, completedLaps: row.laps ?? timing?.completedLaps ?? previous.completedLaps,
        lap: row.laps ?? timing?.lap ?? previous.lap, bestLap: row.bestLap ?? timing?.bestLap ?? previous.bestLap };
    }), lap: Math.max(0, ...result.rows.map(row => row.laps ?? 0)) };
}

export function countdownParts(start: string, serverNow: number): { text: string; due: boolean } {
  const value = Date.parse(start);
  if (!Number.isFinite(value)) return { text: '时间待定', due: false };
  const seconds = Math.max(0, Math.ceil((value - serverNow) / 1000));
  if (!seconds) return { text: '等待开赛确认', due: true };
  const days = Math.floor(seconds / 86400), hours = Math.floor(seconds % 86400 / 3600);
  const minutes = Math.floor(seconds % 3600 / 60), remaining = seconds % 60;
  return { text: `${days ? `${days}天 ` : ''}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`, due: false };
}
